/**
 * Grand Quack Auto — juice system.
 *
 * Visual effects that make the game feel impactful:
 * - Screen shake: camera offset with exponential decay on quack/collision
 * - Slow-mo freeze: reduced timeScale for a few frames on big impacts
 * - FOV punch: brief cinematic widening on big combos
 * - Mother's Rage cinematic: vignette + chromatic aberration shader pass
 *   (tweened), red rim light parented to duck (pulsing), 60-BPM heartbeat
 *
 * See research.md §2 "The juice that makes clips shareable".
 */

import * as THREE from 'three';
import { attachHatWithTracking, getCurrentHatType, type HatType } from '../entities/hats';
import { setHazeStrength, setRageStrength, setShaderTime } from '../shaders/rageFx';
import type { System, World } from '../types';
import { startHeartbeat, stopHeartbeat } from '../utils/audio';

// ── Screen shake state ────────────────────────────────────────────────────

let shakeIntensity = 0;
const SHAKE_DECAY = 0.88;

/** Trigger screen shake. Called by quackSystem, copSystem, etc. */
export function triggerShake(intensity: number): void {
  shakeIntensity = Math.max(shakeIntensity, intensity);
}

// ── Slow-mo state ─────────────────────────────────────────────────────────

let slowMoEndTime = 0;
let slowMoActive = false;
const SLOW_MO_SCALE = 0.15;
const SLOW_MO_DURATION = 0.12; // real seconds

/** Trigger brief slow-motion freeze. Called on big impacts. */
export function triggerSlowMo(world: World): void {
  slowMoEndTime = performance.now() / 1000 + SLOW_MO_DURATION;
  world.time.timeScale = SLOW_MO_SCALE;
  slowMoActive = true;
}

// ── FOV punch (cinematic zoom on big combos) ──────────────────────────────

let fovPunchEnd = 0;
const FOV_PUNCH_AMOUNT = 25; // extra degrees
const FOV_PUNCH_DURATION = 0.5;

/** Brief dramatic FOV widening. Called by comboSystem on x6+ combos. */
export function triggerFovPunch(): void {
  fovPunchEnd = performance.now() / 1000 + FOV_PUNCH_DURATION;
}

// ── Dolly-zoom (Hitchcock effect on x6+ tier crossings) ───────────────────
// Apply AFTER cameraSystem.render has placed the camera. Pulls the camera
// toward the duck while widening FOV — perfect, meme-ready vertigo punch.

let dollyZoomEnd = 0;
const DOLLY_ZOOM_DURATION = 0.4;
const DOLLY_PULL = 1.5;     // world units closer to target at peak
const DOLLY_FOV_KICK = 10;  // extra degrees at peak
const _dollyDir = new THREE.Vector3();

/** Hitchcock dolly-zoom. Called by comboSystem at x6+ tier crossings. */
export function triggerDollyZoom(): void {
  dollyZoomEnd = performance.now() / 1000 + DOLLY_ZOOM_DURATION;
}

// ── Heat haze distortion (mega-quack visual punch) ───────────────────────
let hazeStrength = 0;
const HAZE_DECAY = 0.92;

/** Trigger heat haze on mega-quack. Decays exponentially each frame. */
export function triggerHaze(): void { hazeStrength = 1; }

// ── Mother's Rage cinematic (vignette + rim light + heartbeat) ────────────

let rageStrength = 0;     // current shader uniform value
let rageTarget = 0;       // tween target (1 while raging, 0 otherwise)
const RAGE_ATTACK = 0.3;  // seconds 0→1
const RAGE_DECAY = 0.6;   // seconds 1→0
let rimLight: THREE.PointLight | null = null;
let rageElapsed = 0;      // real-time accumulator for the 6 Hz pulse
let preRageHat: HatType | null = null;

/** Begin Mother's Rage. Called by ducklingSystem when a duckling dies. */
export function enterRage(world: World): void {
  rageTarget = 1;
  if (!rimLight) {
    rimLight = new THREE.PointLight(0xff2233, 4, 6);
    rimLight.position.set(0, 1.2, 0);
  }
  if (rimLight.parent !== world.duck) world.duck.add(rimLight);
  startHeartbeat();
  preRageHat = getCurrentHatType(world.duck);
  attachHatWithTracking(world.duck, 'viking');
}

/** End Mother's Rage. Called when isMotherEnraged() flips false. */
export function exitRage(world?: World): void {
  rageTarget = 0;
  stopHeartbeat();
  if (world && preRageHat) attachHatWithTracking(world.duck, preRageHat);
  preRageHat = null;
}

// ── System ────────────────────────────────────────────────────────────────

export const juiceSystem: System = {
  name: 'juice',

  render(_dt: number, _alpha: number, world: World) {
    // ── Screen shake ──────────────────────────────────────────────────
    if (shakeIntensity > 0.001) {
      const ox = (Math.random() - 0.5) * shakeIntensity;
      const oz = (Math.random() - 0.5) * shakeIntensity;
      world.camera.position.x += ox;
      world.camera.position.z += oz;
      shakeIntensity *= SHAKE_DECAY;
    } else {
      shakeIntensity = 0;
    }

    // ── Slow-mo recovery ──────────────────────────────────────────────
    // Only restore timeScale if slow-mo was the one that set it.
    // Other systems (evolution cinematic, ABSOLUTE QUACKERY) also set
    // timeScale=0 and must not be overridden here.
    const now = performance.now() / 1000;
    if (slowMoActive && now >= slowMoEndTime) {
      // Don't override a harder freeze (evolution=0, ABSOLUTE QUACKERY=0).
      if (world.time.timeScale !== 0) world.time.timeScale = 1;
      slowMoActive = false;
    }

    // ── FOV punch (cinematic zoom) ────────────────────────────────────
    if (now < fovPunchEnd) {
      const t = (fovPunchEnd - now) / FOV_PUNCH_DURATION;
      world.camera.fov += FOV_PUNCH_AMOUNT * t;
      world.camera.updateProjectionMatrix();
    }

    // ── Dolly-zoom (Hitchcock effect — applied on top of cameraSystem) ──
    if (now < dollyZoomEnd) {
      // Bell curve over the duration so we punch in then ease back.
      const t = (dollyZoomEnd - now) / DOLLY_ZOOM_DURATION; // 1 → 0
      const k = Math.sin(t * Math.PI);                      // 0 → 1 → 0
      const target = world.playerMode === 'driving' && world.activeVehicle
        ? world.activeVehicle.group.position
        : world.duck.position;
      _dollyDir.copy(target).sub(world.camera.position);
      _dollyDir.y = 0;
      const len = _dollyDir.length();
      if (len > 0.01) {
        _dollyDir.multiplyScalar(1 / len);
        world.camera.position.addScaledVector(_dollyDir, DOLLY_PULL * k);
      }
      world.camera.fov += DOLLY_FOV_KICK * k;
      world.camera.updateProjectionMatrix();
    }

    // ── Heat haze decay + shader time ──────────────────────────────────
    setShaderTime(now);
    if (hazeStrength > 0.001) { hazeStrength *= HAZE_DECAY; setHazeStrength(hazeStrength); }
    else { hazeStrength = 0; setHazeStrength(0); }

    // ── Mother's Rage tween + rim light pulse ─────────────────────────
    const rageDt = world.time.lastFrame || 1 / 60;
    const rate = rageTarget > rageStrength ? RAGE_ATTACK : RAGE_DECAY;
    rageStrength += (rageTarget - rageStrength) * Math.min(1, rageDt / rate);
    setRageStrength(rageStrength);

    if (rimLight) {
      // 6 Hz pulse modulates intensity so the duck looks like it's breathing
      // anger. Always nonzero while strength > 0 so the rim never disappears.
      rageElapsed += rageDt;
      const pulse = 0.6 + 0.4 * Math.sin(rageElapsed * Math.PI * 2 * 6);
      rimLight.intensity = 4 * pulse * rageStrength;
      // Detach once the decay tween has fully unwound — cheaper than leaving
      // an unused light parented to the duck across the entire session.
      if (rageStrength < 0.01 && rageTarget === 0 && rimLight.parent) {
        rimLight.parent.remove(rimLight);
      }
    }
  },
};
